import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for managing page titles, descriptions, meta tags, and breadcrumbs
 * Usage: <SEO title="Page Title" description="Page description" breadcrumbs={[...]} />
 */
const SEO = ({ 
  title, 
  description, 
  keywords,
  ogImage = '/ascarii-logo.png',
  ogType = 'website',
  canonical,
  noindex = false,
  breadcrumbs,
  faqSchema
}) => {
  const defaultTitle = 'Ascarii - SAP Business One Partner | Cloud ERP Solutions UK';
  const defaultDescription = 'SAP Premier Partner delivering cloud-first SAP Business One ERP solutions for SMEs. Expert implementation, 99.995% uptime AWS hosting, 15+ years experience.';
  const baseUrl = 'https://www.ascarii.com';
  
  const fullTitle = title ? `${title} | Ascarii` : defaultTitle;
  const metaDescription = description || defaultDescription;
  const currentUrl = typeof window !== 'undefined' ? window.location.href : baseUrl;

  // Generate breadcrumb schema if provided
  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url ? `${baseUrl}${crumb.url}` : undefined
    }))
  } : null;

  // Generate FAQ schema if provided
  const faqJsonLd = faqSchema ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqSchema.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  } : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={currentUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={metaDescription} />
      <meta property="twitter:image" content={ogImage} />

      {/* Breadcrumb Schema */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {/* FAQ Schema */}
      {faqJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(faqJsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
